import React, { useEffect, useRef } from 'react'
import { SparklesIcon, XIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/shared/components/ui/button'
import {
	Empty,
	EmptyContent,
	EmptyDescription,
	EmptyHeader,
	EmptyTitle,
} from '@/shared/components/ui/empty'
import { Item, ItemContent, ItemTitle } from '@/shared/components/ui/item'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { ampli } from '@/shared/lib/ampli'
import { cn } from '@/shared/lib/utils'

import { useAssistantConversation } from '../lib/useAssistantConversation'
import ChatFooter from './ChatFooter'
import ChatHeader from './ChatHeader'
import ChatMessages from './ChatMessages'

export default function Chat({ close }: { close: () => void }) {
	const { t } = useTranslation()

	useEffect(() => {
		ampli.aiChatOpened()

		return () => {
			ampli.aiChatClosed()
		}
	}, [])
	const containerRef = useRef<HTMLDivElement | null>(null)
	const messagesListRef = useRef<HTMLDivElement | null>(null)
	const { agent, avaliableAgents, setAgent } = useAssistantConversation()
	const refs = {
		messagesListRef,
		containerRef,
	}

	return (
		<div
			className='flex h-[90vh] w-[512px] flex-col overflow-hidden rounded-2xl bg-card'
			ref={containerRef}
		>
			{agent ? (
				<>
					<ChatHeader refs={refs} handleClose={close} />
					<div className='relative mx-auto flex w-full flex-1 shrink-0 grow basis-0 flex-col items-start'>
						<ChatMessages refs={refs} />
						<ChatFooter />
					</div>
				</>
			) : (
				<>
					<div className='flex items-center justify-between rounded-t-lg p-4'>
						<div className='flex items-center gap-2'>
							<SparklesIcon className='size-4 text-primary' />
							<h3 className='text-base'>
								{t('app.assistant', { defaultValue: 'AI Assistant' })}
							</h3>
						</div>
						<Tooltip>
							<TooltipTrigger asChild>
								<Button
									onClick={() => close()}
									data-testid='chat-close'
									variant='ghost'
									size='icon'
								>
									<XIcon />
								</Button>
							</TooltipTrigger>
							<TooltipContent>
								{t('ai.assistant.close_chat', {
									defaultValue: 'Close chat',
								})}
							</TooltipContent>
						</Tooltip>
					</div>
					<Empty>
						<EmptyHeader>
							<EmptyTitle>
								{t('ai.assistant.select_agent', {
									defaultValue: 'Select an agent',
								})}
							</EmptyTitle>
							<EmptyDescription>
								{t('ai.assistant.select_agent_description', {
									defaultValue: 'Select an agent to start a conversation',
								})}
							</EmptyDescription>
						</EmptyHeader>
						<EmptyContent>
							<div className='flex h-full justify-center gap-2'>
								{avaliableAgents
									.filter((_agent) => _agent.is_enabled)
									.map((agent) => (
										<Item
											className={cn(
												'w-full cursor-pointer rounded-lg border border-transparent bg-accent/50 p-5 text-left text-sm font-medium hover:border-border hover:bg-accent/70',
												{
													'cursor-not-allowed opacity-50': !agent.is_enabled,
												}
											)}
											asChild
											key={agent.uri}
											variant='muted'
											size='sm'
										>
											<button
												key={agent.uri}
												type='button'
												onClick={() => setAgent(agent.uri)}
												disabled={!agent.is_enabled}
											>
												<ItemContent>
													<ItemTitle>
														{t(agent.name, { defaultValue: agent.name })}
													</ItemTitle>
												</ItemContent>
											</button>
										</Item>
									))}
							</div>
						</EmptyContent>
					</Empty>
				</>
			)}
		</div>
	)
}
